import React, { useRef, useEffect, useState } from 'react';
import maplibregl from 'maplibre-gl';
import "@maptiler/geocoding-control/style.css";
import 'maplibre-gl/dist/maplibre-gl.css';
import { t } from 'i18next';

interface MapComponentProps {
  markers: MarkerImport[];
  onMarkerSelected: (selectedMarkerID: number | null) => void;
}

type MarkerImport = {
  id: number;
  title?: string;
  isOpen: boolean;
  isPartner: boolean;
  restaurant__gps_lat: number;
  restaurant__gps_lon: number;
};

const CategoryMap: React.FC<MapComponentProps> = ({ markers, onMarkerSelected }) => {
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const geolocateControlRef = useRef<maplibregl.GeolocateControl | null>(null);
  const graz = { lng: 15.4395, lat: 47.0707 };
  const [zoom] = useState(13);
  const [language] = useState(typeof window !== 'undefined' ? localStorage.getItem('i18nextLng') || 'de' : 'de');

  const categoryMarkers = useRef<MarkerImport[]>(markers);
  const markersOnMap = useRef<maplibregl.Marker[]>([]);
  const oldSelectedMarkerIndex = useRef(-1);
  const selectedMarkerIndex = useRef(-1);
  const markerSelectionRef = useRef(false);
  const showClosedRef = useRef(true);
  const [showClosed, setShowClosed] = useState(true);
  const [mapController, setMapController] = useState<any>(null);
  const [GeocodingControl, setGeocodingControl] = useState<any>(null);
  const [selectedArea, setSelectedArea] = useState<string | null>(null);

  const AREA_COORDS: Record<string, { center: [number, number]; zoom: number }> = {
    Hauptplatz:            { center: [15.4382, 47.0709], zoom: 16 },
    'Kaiser-Josef-Platz':  { center: [15.4467, 47.0685], zoom: 16 },
    'Herz-Jesu-Viertel':   { center: [15.4555, 47.0695], zoom: 15 },
    Univiertel:            { center: [15.4508, 47.0754], zoom: 15 },
    Lendplatz:             { center: [15.4301, 47.0755], zoom: 16 },
  };

  const handleAreaClick = (placeName: string) => {
    setSelectedArea(placeName);
    const coords = AREA_COORDS[placeName];
    if (coords) {
      map.current?.flyTo(coords);
    }
  };

  const handleMapClick = () => {
    if (!markerSelectionRef.current) {
      selectedMarkerIndex.current = -1;
    }

    if (selectedMarkerIndex.current !== oldSelectedMarkerIndex.current) {
      resetMarkers();
      oldSelectedMarkerIndex.current = selectedMarkerIndex.current;

      if (selectedMarkerIndex.current >= 0) {
        onMarkerSelected(categoryMarkers.current[selectedMarkerIndex.current].id);
      } else {
        onMarkerSelected(-1);
      }
    }
    markerSelectionRef.current = false;
  };

  const resetMarkers = () => {
    if (map.current) {
      markersOnMap.current.forEach((marker) => marker.remove());
      markersOnMap.current = [];
      addMarkers();
    }
  };

  const addMarkers = () => {
    if (!map.current) return;

    const newMarkers: maplibregl.Marker[] = [];

    for (let i = 0; i < categoryMarkers.current.length; i++) {
      if (!showClosedRef.current && !categoryMarkers.current[i].isOpen) continue;

      // DOM-Element für Marker erstellen
      const el = document.createElement("div");
      el.className = "marker";
      // richtige Icon-URL abhängig von Partner- und Open-Status wählen
      let iconUrl = "";
      if (selectedMarkerIndex.current === i) {
        iconUrl = categoryMarkers.current[i].isPartner
          ? "/icons/marker_partner_selected2.svg"
          : "/icons/marker_selected.svg";
      }
      else {
        if (categoryMarkers.current[i].isPartner) {
          iconUrl = categoryMarkers.current[i].isOpen
            ? "/icons/marker_partner_open2.svg"
            : "/icons/marker_partner_closed2.svg";
        } else {
          iconUrl = categoryMarkers.current[i].isOpen
            ? "/icons/marker_open.svg"
            : "/icons/marker_closed.svg";
        }
      }

      // Standard-Icon (dein SVG)
      el.style.backgroundImage = `url(${iconUrl})`;
      el.style.backgroundSize = "contain"; // sorgt dafür, dass SVG skaliert wird
      el.style.backgroundRepeat = "no-repeat";
      el.style.cursor = "pointer";

      if (categoryMarkers.current[i].isPartner) {
        el.style.width = "35px";
        el.style.height = "45px";
      } else {
        el.style.width = "27px";
        el.style.height = "40px";
      }

      // Klicklistener hinzufügen
      el.addEventListener("click", markerClick(i));

      // "selected"-Zustand visuell kennzeichnen
      if (selectedMarkerIndex.current === i) {
        el.classList.add("selected");
      }

      // Marker erzeugen
      const newMarker = new maplibregl.Marker({ element: el, draggable: false })
        .setLngLat([
          categoryMarkers.current[i].restaurant__gps_lon,
          categoryMarkers.current[i].restaurant__gps_lat,
        ])
        .addTo(map.current!);

      newMarkers.push(newMarker);
    }

    markersOnMap.current = newMarkers;
  };

  const markerClick = (index: number) => () => {
    selectedMarkerIndex.current = index;
    markerSelectionRef.current = true;
  };

  const toggleShowClosed = () => {
    const newVal = !showClosedRef.current;
    showClosedRef.current = newVal;
    setShowClosed(newVal);
    if (
      !newVal &&
      selectedMarkerIndex.current >= 0 &&
      !categoryMarkers.current[selectedMarkerIndex.current].isOpen
    ) {
      selectedMarkerIndex.current = -1;
      onMarkerSelected(null);
    }
    resetMarkers();
  };

  useEffect(() => {
    if (map.current) return;

    map.current = new maplibregl.Map({
      container: mapContainer.current!,
      style: `https://api.maptiler.com/maps/basic/style.json?key=KHWONvKvBhvZQOL37lv8`,
      center: [graz.lng, graz.lat],
      zoom: zoom,
      attributionControl: false,
    });

    map.current.on('click', handleMapClick);

    setTimeout(() => {
      map.current?.resize();
      const geolocateControl = new maplibregl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: false,
        showAccuracyCircle: true,
      });
      geolocateControlRef.current = geolocateControl;
      map.current?.addControl(geolocateControl, 'bottom-left');
    }, 500);

    addMarkers();

    // client side import as this are not working with SSR
    import("@maptiler/geocoding-control/maplibregl-controller").then((module) => {
      setMapController(module.createMapLibreGlMapController(map.current!, maplibregl));
    });
    import("@maptiler/geocoding-control/react").then((module) => {
      setGeocodingControl(() => module.GeocodingControl);
    });
  }, [graz.lng, graz.lat, zoom]);

  useEffect(() => {
    categoryMarkers.current = markers;
    selectedMarkerIndex.current = -1;
    resetMarkers();
  }, [markers]);

  const triggerGeolocate = () => {
    geolocateControlRef.current?.trigger();
  };

  return (
    <div className="map-wrap">
      <div className="map-geocoding">
        {GeocodingControl && (
          <GeocodingControl
            apiKey={'KHWONvKvBhvZQOL37lv8'}
            selectFirst={false}
            mapController={mapController}
            bbox={[15.35, 47.00, 15.55, 47.15]}
            limit={5}
            language={language}
            placeholder={t('placesearch')}
            noResultsMessage={t('noplaceresult')}
          />
        )}
        <button
          className="map-geolocate-btn"
          onClick={triggerGeolocate}
          title={t('location')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
            <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94-3.97c-.82-1.18-2.04-2.17-3.43-2.8.35 1.11.53 2.28.53 3.48 0 1.45-.24 2.85-.68 4.15 1.32.57 2.49 1.5 3.27 2.68.36-.75.68-1.53.94-2.32.26-.79.48-1.6.64-2.42.16-.8.25-1.63.25-2.48 0-1.04-.1-2.05-.29-3.03l-.23-.26z" />
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
          </svg>
        </button>
      </div>
      <div className="map-filter-bar">
      <button
        className={`map-toggle-closed-btn${showClosed ? '' : ' map-toggle-closed-btn--hidden'}`}
        onClick={toggleShowClosed}
        title={showClosed ? t('hideclosed') : t('showclosed')}
      >
        {showClosed ? (
          <>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zm0 12.5c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
            </svg>
            <span>{t('closed')}</span>
          </>
        ) : (
          <>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
            </svg>
            <span>{t('closed')}</span>
          </>
        )}
      </button>
      <button className={`map-area-btn${selectedArea === 'Hauptplatz' ? ' map-area-btn--active' : ''}`} onClick={() => handleAreaClick('Hauptplatz')}>🕰️ Hauptplatz</button>
      <button className={`map-area-btn${selectedArea === 'Kaiser-Josef-Platz' ? ' map-area-btn--active' : ''}`} onClick={() => handleAreaClick('Kaiser-Josef-Platz')}>👑 Kaiser-Joseph-Platz</button>
      <button className={`map-area-btn${selectedArea === 'Lendplatz' ? ' map-area-btn--active' : ''}`} onClick={() => handleAreaClick('Lendplatz')}>🚒 Lendplatz</button>
      <button className={`map-area-btn${selectedArea === 'Univiertel' ? ' map-area-btn--active' : ''}`} onClick={() => handleAreaClick('Univiertel')}>👩‍🎓 Univiertel</button>
      </div>
      <div ref={mapContainer} className="map" />
    </div>
  );
};

export default CategoryMap;